import styles from "../../styles";
import { GameObjectReference } from "@shared/types";
import { AchievementItemComponent } from "../achievement/AchievementComponent";

export class AchievementGridComponent {
    private readonly _objects: GameObjectReference[];

    public constructor(objects: GameObjectReference[]) {
        this._objects = objects;
    }

    private createLayer(layerNumber: number): HTMLElement {
        const layerContainer: HTMLElement = document.createElement("div");
        layerContainer.className = "achievement-layer";

        const styleElement: HTMLStyleElement = document.createElement("style");
        styleElement.textContent = styles.achievement;
        layerContainer.appendChild(styleElement);

        // Define layer ranges
        const layerRanges: Record<number, [number, number]> = {
            1: [0, 10],
            2: [11, 20],
            3: [21, 30],
        };

        // Get range for the requested layer
        const [min, max] = layerRanges[layerNumber];

        // Filter and sort objects based on their layer value
        const layerObjects: GameObjectReference[] = this._objects
            .filter((obj: GameObjectReference): boolean => obj.layer >= min && obj.layer <= max)
            .sort((a, b) => a.layer - b.layer); // Ensure lowest layer number comes first

        layerObjects.forEach((obj: GameObjectReference, index: number): void => {
            const itemRow: HTMLDivElement = document.createElement("div");
            itemRow.className = "achievement-item-row";

            const itemComponent: AchievementItemComponent = new AchievementItemComponent(obj);
            const itemElement: HTMLElement = itemComponent.createElement();
            itemRow.appendChild(itemElement);

            // Add arrow if not last item
            if (index < layerObjects.length - 1) {
                const arrow: HTMLElement = document.createElement("div");
                arrow.className = "achievement-arrow";
                arrow.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" 
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                </svg>`;
                itemRow.appendChild(arrow);
            }

            layerContainer.appendChild(itemRow);
        });

        return layerContainer;
    }

    private createNavigationButtons(switchLayerCallback: (layer: number) => void): HTMLElement {
        const navContainer: HTMLElement = document.createElement("div");
        navContainer.className = "layer-navigation";

        const styleElement: HTMLStyleElement = document.createElement("style");
        styleElement.textContent = styles.achievement;
        navContainer.appendChild(styleElement);

        [1, 2, 3].forEach((layer: number): void => {
            const button: HTMLButtonElement = document.createElement("button");
            button.innerText = `Escape route ${layer}`;
            button.onclick = (): void => switchLayerCallback(layer);
            navContainer.appendChild(button);
        });

        return navContainer;
    }

    public createElement(gridContainer: HTMLElement): HTMLElement {
        const styleElement: HTMLStyleElement = document.createElement("style");
        styleElement.textContent = styles.achievement;
        gridContainer.appendChild(styleElement);

        const contentContainer: HTMLElement = document.createElement("div");
        contentContainer.className = "achievement-content";
        const switchLayer: (layerNumber: number) => void = (layerNumber: number): void => {
            contentContainer.innerHTML = "";
            const layerElement: HTMLElement = this.createLayer(layerNumber);
            contentContainer.appendChild(layerElement);
        };

        const navigation: HTMLElement = this.createNavigationButtons(switchLayer);
        gridContainer.appendChild(navigation);

        switchLayer(1);
        gridContainer.appendChild(contentContainer);

        return gridContainer;
    }
}
